import React, { useState, useEffect } from 'react';
import './ValentineQuiz.css';
import quizData from '../../data/quiz-questions.json';
import promptLogData from '../../data/prompt-log.json';

function ValentineQuiz({ onBackToLanding }) {
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [showPromptLog, setShowPromptLog] = useState(false);
  const [promptData, setPromptData] = useState([]);

  // Initialize quiz with 5 random questions
  useEffect(() => {
    const shuffled = [...quizData.questions].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, 5);
    setSelectedQuestions(selected);
    setAnswers(new Array(5).fill(null));
  }, []);

  const currentQuestion = selectedQuestions[currentQuestionIndex];

  const handleAnswerSelect = (answerIndex) => {
    if (showResult) return;
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = {
      questionId: currentQuestion.id,
      selected: selectedAnswer,
      correct: currentQuestion.correctAnswer,
      isCorrect
    };
    setAnswers(newAnswers);

    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
    } else {
      setShowCorrectAnswer(true);
    }

    setShowResult(true);

    // Auto-advance after showing result
    setTimeout(() => {
      if (currentQuestionIndex < selectedQuestions.length - 1) {
        moveToNextQuestion();
      } else {
        setIsComplete(true);
      }
    }, 2000);
  };

  const moveToNextQuestion = () => {
    setCurrentQuestionIndex(prev => prev + 1);
    setSelectedAnswer(null);
    setShowResult(false);
    setShowCorrectAnswer(false);
  };

  const resetQuiz = () => {
    const shuffled = [...quizData.questions].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, 5);
    setSelectedQuestions(selected);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setCorrectAnswers(0);
    setIsComplete(false);
    setShowCorrectAnswer(false);
    setAnswers(new Array(5).fill(null));
    setShowPromptLog(false);
  };

  const togglePromptLog = () => {
    if (!showPromptLog && promptData.length === 0) {
      setPromptData(promptLogData.prompts);
    }
    setShowPromptLog(!showPromptLog);
  };

  const isPerfectScore = correctAnswers === 5;

  if (!selectedQuestions.length) {
    return <div className="loading">Loading your Valentine's quiz...</div>;
  }

  if (isComplete) {
    return (
      <div className="quiz-results">
        <div className="results-container fade-in">
          {isPerfectScore ? (
            <div className="success-result">
              <div className="big-ornament pulse">♦</div>
              <h1 className="success-title bounce">Congratulations, Steeny!</h1>
              <p className="success-message">
                You achieved perfection: {correctAnswers} out of 5 questions correct.
              </p>
              <div className="valentine-declaration">
                <h2>YOU ARE OFFICIALLY MY VALENTINE</h2>
                <p>Your intimate knowledge of our shared story is flawless.</p>
                <div className="elegant-flourish">♦ ♦ ♦</div>
              </div>
            </div>
          ) : (
            <div className="retry-result">
              <div className="elegant-mark">◊</div>
              <h2 className="retry-title">Almost there, my darling</h2>
              <p className="retry-message">
                You answered {correctAnswers} out of 5 questions correctly.
              </p>
              <p className="gentle-message">
                Perfection is required to claim your place as my Valentine.
                <br />
                <em>Try once more...</em>
              </p>
            </div>
          )}
          
          <div className="result-buttons">
            <button 
              className="quiz-button primary-button"
              onClick={resetQuiz}
            >
              {isPerfectScore ? "Experience Again" : "Try Once More"}
            </button>
          </div>
          
          {isPerfectScore && (
            <div className="journey-button-container">
              <button 
                className="journey-button"
                onClick={togglePromptLog}
              >
                {showPromptLog ? "Hide Journey" : "Show Journey"}
              </button>
            </div>
          )}
          
          {showPromptLog && isPerfectScore && (
            <div className="prompt-log-section">
              <div className="prompt-log-header">
                <h3 className="prompt-log-title">How We Built This Together</h3>
                <button className="close-button" onClick={togglePromptLog}>✕</button>
              </div>
              <p className="prompt-log-description">
                Every conversation, every request, every small change that made this Valentine's surprise just right.
              </p>
              <div className="prompt-log-content">
                <div className="message-chain">
                  {promptData.map((prompt, index) => (
                    <div key={prompt.id} className="message-item">
                      <div className="message-header">
                        <span className="message-number">#{prompt.id}</span>
                        <span className="message-timestamp">{prompt.timestamp}</span>
                      </div>
                      <div className="message-body">
                        <div className="message-prompt">"{prompt.prompt}"</div>
                        <div className="message-status">{prompt.status}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="valentine-quiz">
      <div className="quiz-container fade-in">
        <div className="quiz-header">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / selectedQuestions.length) * 100}%` }}
            ></div>
          </div>
          <div className="progress-text">
            Question {currentQuestionIndex + 1} of {selectedQuestions.length}
          </div>
        </div>

        <div className="question-container slide-in">
          <h2 className="question-text">{currentQuestion.question}</h2>
          
          <div className="answers-grid">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                className={`answer-option ${
                  selectedAnswer === index ? 'selected' : ''
                } ${
                  showResult && index === currentQuestion.correctAnswer ? 'correct' : ''
                } ${
                  showResult && 
                  selectedAnswer === index && 
                  index !== currentQuestion.correctAnswer ? 'incorrect' : ''
                }`}
                onClick={() => handleAnswerSelect(index)}
                disabled={showResult}
              >
                <span className="option-text">{option}</span>
                {showResult && index === currentQuestion.correctAnswer && (
                  <span className="correct-indicator">✓</span>
                )}
              </button>
            ))}
          </div>

          {!showResult && (
            <button 
              className="quiz-button submit-button"
              onClick={handleSubmitAnswer}
              disabled={selectedAnswer === null}
            >
              Submit Answer
            </button>
          )}

          {showResult && (
            <div className="result-feedback fade-in">
              {selectedAnswer === currentQuestion.correctAnswer ? (
                <div className="correct-feedback">
                  <span className="feedback-icon">♦</span>
                  <span>Exquisite. Your knowledge is impeccable.</span>
                </div>
              ) : (
                <div className="incorrect-feedback">
                  <span className="feedback-icon">◊</span>
                  <span>The truth is revealed above.</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="quiz-stats">
          <div className="stat">
            <span className="stat-value">{correctAnswers}</span>
            <span className="stat-label">Correct</span>
          </div>
          <div className="stat">
            <span className="stat-value">{answers.filter(a => a && !a.isCorrect).length}</span>
            <span className="stat-label">Wrong</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ValentineQuiz;
